import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { login } from "../services/api";
import { useAuth } from "../context/AuthContext";
import { useAppConfig } from "../context/AppConfigContext";
import { useTranslation } from "../i18n/LanguageContext";
import LanguageSwitcher from "../components/LanguageSwitcher";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { loginUser } = useAuth();
  const { appName, branding, loginHeader, loginLogo } = useAppConfig();
  const { t, i18nEnabled } = useTranslation();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await login({ email, password });
      loginUser(res.data.token, res.data.user);
      const pendingCard = sessionStorage.getItem("pending_card_code");
      if (pendingCard) {
        navigate(`/c/${pendingCard}`, { replace: true });
      } else {
        navigate("/app");
      }
    } catch (err) {
      setError(
        err.response?.data?.error || t("login.failed")
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="login-page" className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div id="login-card" className="w-full max-w-md bg-white rounded-2xl shadow-lg p-8">
        {i18nEnabled && (
          <div className="flex justify-end mb-2">
            <LanguageSwitcher />
          </div>
        )}
        {loginHeader === "logo" ? (
          <div className="flex justify-center mb-2">
            <img
              src={loginLogo || branding.logo}
              alt={appName}
              className="h-36 object-contain"
              onError={(e) => { e.target.style.display = "none"; }}
            />
          </div>
        ) : (
          <h1 id="login-title" className="text-3xl font-bold text-center text-gray-900 mb-2">
            {appName}
          </h1>
        )}
        <p className="text-center text-gray-500 mb-8">
          {t("login.title")}
        </p>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
            {error}
          </div>
        )}

        <form id="login-form" onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              {t("login.email")}
            </label>
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="you@example.com"
            />
          </div>

          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              {t("login.password")}
            </label>
            <input
              id="password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? t("login.submitting") : t("login.submit")}
          </button>
        </form>

        <p className="mt-3 text-center">
          <Link
            to="/forgot-password"
            className="text-sm text-gray-400 hover:text-blue-500 transition-colors"
          >
            {t("login.forgotPassword")}
          </Link>
        </p>

        <p className="mt-6 text-center text-sm text-gray-500">
          {t("login.noAccount")}{" "}
          <Link
            to="/register"
            className="text-blue-600 font-medium hover:text-blue-500"
          >
            {t("login.signUp")}
          </Link>
        </p>
      </div>
    </div>
  );
}
